

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Box, CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption.js";
import { BASE_URL } from "../../../../components/BaseUrl.js";
import TruncatedCell from "../../../../components/TruncatedCell.js";
import TaskDetails from "./TaskDetails"; // Import the TaskDetails component
import {useTranslation} from "react-i18next";
import CustomText, { textStyles } from "../../../../components/CustomText";
import { convertUTCToLocalDateTime } from "../../../../components/dateTimeUtils";
import {
  getStatusBadgeBackgroundColor,
  getStatusBadgeForegroundColor,
} from "../../../../constants/statusPalette";
const localizer = momentLocalizer(moment);

const ISO_WITHOUT_TIMEZONE_REGEX =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?$/;

const normalizeUTCInput = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (ISO_WITHOUT_TIMEZONE_REGEX.test(trimmed)) return `${trimmed}Z`;
  return trimmed;
};

const toCalendarDate = (value) => new Date(normalizeUTCInput(value));

const formatLocalTimePart = (value) => {
  const formatted = convertUTCToLocalDateTime(value, {
    locale: "en-GB",
    formatOptions: {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    },
  });
  return formatted === "—" ? "--" : formatted;
};

const CalendarTask = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null); // To store selected task
  const [showTaskDetails, setShowTaskDetails] = useState(false); // Toggle between calendar and task details
  const [view, setView] = useState("month");
  const [byDateTasks, setByDateTasks] = useState([]);
  const [byDateMeta, setByDateMeta] = useState({ dateLabel: "", workerLabel: "" });
const { t } = useTranslation();
  const token = useSelector((state) => state.auth.token);
  const { company } = useSelector((state) => state.auth);
  const companyId = company?.id;
  const decryptedToken = token ? decryptToken(token) : null;

  const fetchTasks = useCallback(async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/task-management/admin/tasks?page=1&page_size=100&show_history=true`,
        {
          headers: {
            Authorization: `Bearer ${decryptedToken}`,
          },
        }
      );
      const json = await res.json();
      if (!json.error) {
        const mappedEvents = json.data.tasks.map((task) => {
          const workersArray = Array.isArray(task.assigned_workers) ? task.assigned_workers : [];
          const workerNames = workersArray.map((w) => w && w.worker_name).filter(Boolean).join(", ");
          return {
            id: task.id,
            title: task.title,
            start: toCalendarDate(task.start_at),
            end: toCalendarDate(task.end_at),
            startAtRaw: task.start_at,
            endAtRaw: task.end_at,
            status: task.status,
            worker: workerNames,
            workers: workersArray,
          };
        });
        setEvents(mappedEvents);
      }
    } catch (err) {
      console.error("❌ Error fetching tasks:", err);
    } finally {
      setLoading(false);
    }
  }, [decryptedToken]);

  useEffect(() => {
    const reload = () => { setLoading(true); fetchTasks(); };
    if (decryptedToken) fetchTasks();
    window.addEventListener("tasks:updated", reload);
    return () => window.removeEventListener("tasks:updated", reload);
  }, [decryptedToken, fetchTasks]);

  const eventStyleGetter = (event) => {
    const statusKey = event.status || "assigned";
    return {
      style: {
        backgroundColor: getStatusBadgeBackgroundColor(statusKey),
        color: getStatusBadgeForegroundColor(statusKey),
        borderRadius: "6px",
        border: "0px",
        paddingLeft: "4px",
      },
    };
  };

  const handleEventClick = async (event) => {
    try {
      const res = await fetch(
        `${BASE_URL}/task-management/admin/tasks/${event.id}`,
        {
          headers: { Authorization: `Bearer ${decryptedToken}` },
        }
      );
      const data = await res.json();
      if (!data.error) {
        setSelectedTask(data.data.task); // full task object
        setShowTaskDetails(true);
      }
    } catch (err) {
      console.error("❌ Error fetching task details:", err);
    }
  };

  const handleBackToCalendar = () => {
    setShowTaskDetails(false);
    setSelectedTask(null);
    setByDateTasks([]);
    setByDateMeta({ dateLabel: "", workerLabel: "" });
  };

  // Hide default time in grid views; keep in agenda
  const formats = useMemo(() => (
    view === "agenda" ? {} : { eventTimeRangeFormat: () => "" }
  ), [view]);

  const handleNameClick = async (event) => {
    try {
      const firstWorkerId = event.workers?.[0]?.worker_id;
      if (!firstWorkerId || !companyId) return;
      const date = moment(event.start).format("YYYY-MM-DD");
      const res = await fetch(`${BASE_URL}/task-management/admin/tasks/by-date?worker_id=${firstWorkerId}&company_id=${companyId}&date=${date}` , {
        headers: { Authorization: `Bearer ${decryptedToken}` },
      });
      const data = await res.json();
      const tasks = Array.isArray(data?.data?.tasks) ? data.data.tasks : [];
      // normalize for list
      const normalized = tasks.map((item) => ({
        id: item?.task?.id || item?.id,
        title: item?.task?.title || item?.title,
        start_at: item?.task?.start_at || item?.start_at,
        end_at: item?.task?.end_at || item?.end_at,
      })).filter((t) => !!t.id);
      setByDateTasks(normalized);
      setByDateMeta({ dateLabel: date, workerLabel: event.worker || "" });
      if (normalized[0]?.id) {
        // fetch full details like title click
        const res2 = await fetch(`${BASE_URL}/task-management/admin/tasks/${normalized[0].id}`, { headers: { Authorization: `Bearer ${decryptedToken}` } });
        const data2 = await res2.json();
        if (!data2.error) {
          setSelectedTask(data2.data.task);
          setShowTaskDetails(true);
        }
      }
    } catch (e) {
      console.error("by-date fetch failed", e);
    }
  };

  return (
    <Box
      sx={{
        height: "80vh",
        p: 2,
        background: "#fff",
        borderRadius: "8px",
        overflowY: "auto",
      }}
    >
      <CustomText
        sx={{
          ...textStyles.h1,
          fontSize: 18,
          fontWeight: 600,
          color: "#003149",
          textAlign: "left",
          mb: 2,
        }}
      >
        {t("sidebar.taskManagement")}
      </CustomText>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {showTaskDetails ? (
            <Box sx={{ display: { xs: 'block', md: 'grid' }, gridTemplateColumns: byDateTasks.length ? '300px 1fr' : '1fr', gap: 2 }}>
              {byDateTasks.length > 0 && (
                <Box sx={{ background: '#fff', borderRadius: 2, p: 1, maxHeight: '80vh', overflowY: 'auto', border: '1px solid #E5E7EB' }}>
                  {byDateMeta?.dateLabel && (
                    <Box sx={{ px: 1, py: 1, fontSize: 12, color: '#64748B' }}>Tasks on {byDateMeta.dateLabel}</Box>
                  )}
                  {byDateTasks.map((t) => (
                    <Box key={t.id} sx={{ px: 1.2, py: 1, borderRadius: 1, cursor: 'pointer', backgroundColor: selectedTask?.id === t.id ? '#F1F5F9' : 'transparent', '&:hover': { backgroundColor: '#F8FAFC' } }} onClick={async () => {
                      try {
                        const res3 = await fetch(`${BASE_URL}/task-management/admin/tasks/${t.id}`, { headers: { Authorization: `Bearer ${decryptedToken}` } });
                        const data3 = await res3.json();
                        if (!data3.error) setSelectedTask(data3.data.task);
                      } catch {}
                    }}>
                      <Box sx={{ fontSize: 13, fontWeight: 600, color: '#0F172A', mb: 0.25 }}>{t.title}</Box>
                      <Box sx={{ fontSize: 12, color: '#64748B' }}>
                        {`${formatLocalTimePart(t.start_at)} - ${formatLocalTimePart(t.end_at)}`}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              <Box>
                <TaskDetails task={selectedTask} onBack={handleBackToCalendar} />
              </Box>
            </Box>
          ) : (
            <Calendar
              localizer={localizer}
              events={events}
              defaultView="month"
              view={view}
              views={["day", "week", "month", "agenda"]}
              startAccessor="start"
              endAccessor="end"
              style={{ height: "100%" }}
              eventPropGetter={eventStyleGetter}
              onSelectEvent={handleEventClick}
              onView={(v) => setView(v)}
              length={view === "agenda" ? 365 : undefined}
            messages={{
  today: t("calendar.today"),
  previous: t("calendar.back"),
  next: t("calendar.next"),
  month: t("calendar.month"),
  week: t("calendar.week"),
  day: t("calendar.day"),
  agenda: t("calendar.year")
}}
              formats={formats}
              components={{
                event: ({ event }) => {
                  const fg = getStatusBadgeForegroundColor(event.status || "assigned");
                  return (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                      <span onClick={(e)=>{e.stopPropagation(); handleEventClick(event);}} style={{ cursor: 'pointer', flex:1 }}>
                        <TruncatedCell text={event.title} sx={{ color: fg, textAlign: 'left' }} />
                      </span>
                      <small style={{ whiteSpace: 'nowrap', marginLeft: 6, color: fg }}>
                        {formatLocalTimePart(event.startAtRaw || event.start)} - {formatLocalTimePart(event.endAtRaw || event.end)}
                      </small>
                    </Box>
                    {event.worker ? (
                      <Box sx={{ mt: 0.5 }}>
                        <span onClick={(e)=>{e.stopPropagation(); handleNameClick(event);}} style={{ cursor: 'pointer', opacity: 0.95 }}>
                          <TruncatedCell text={event.worker} sx={{ color: fg, textAlign: 'left' }} />
                        </span>
                      </Box>
                    ) : null}
                  </Box>
                  );
                },
              }}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default CalendarTask;
